import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { AlertCircle, ArrowRight, Check, MailCheck, Building2, Lock, Search } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { api, ApiError } from '../services/api';
import { Logo } from '../components/Logo';
import { Button } from '../components/Button';
import { Card } from '../components/Card';
import { Alert } from '../components/Alert';
import { Loader } from '../components/Loader';
import { Input } from '../components/Input';
import { CheckoutBilling, CheckoutBillingInput, CheckoutView, PriceInterval, UUID } from '../types';
import { EmbeddedPayment } from '../components/EmbeddedPayment';
import { PeriodPrice, Saving, periodsToOffer, savingFor } from '../lib/checkoutPeriods';

/**
 * SPEC-07 self-serve checkout.
 *
 * Signed-out visitors go to the Nexus sign-up page (which links to sign-in),
 * then back here with the plan preserved. Paid plans
 * collect billing details, then hand over to Stripe; `/checkout/return` is where
 * Stripe sends the customer back, and it activates only on Stripe's word.
 */

const TERMS_URL = 'https://cyrn.fr/conditions-generales.html';

type Lang = 'fr' | 'en';

const T = {
  fr: {
    loading: 'Préparation de votre commande…',
    redirecting: 'Connexion à votre compte CYRN…',
    summary: 'Votre offre',
    free: 'Gratuit',
    perMonth: '/ mois',
    perQuarter: '/ trimestre',
    perYear: '/ an',
    each: 'chacun',
    websites: 'sites',
    offer: 'Offre réservée à votre organisation',
    monthly: 'Mensuel',
    quarterly: 'Trimestriel',
    yearly: 'Annuel',
    saveAmount: 'économisez {amount} ({percent} %)',
    monthsFree: '{n} mois offerts',
    saveSummary: 'Vous économisez {amount} ({percent} %)',
    savingVsMonthly: 'par rapport au paiement mensuel',
    vatNote: 'TVA non applicable, art. 293 B du CGI',
    org: 'Organisation',
    newOrg: 'Une nouvelle organisation',
    start: 'Commencer gratuitement',
    payUnavailable:
      'Le paiement en ligne arrive très bientôt. Pour souscrire dès maintenant, écrivez-nous à contact@cyrn.fr.',
    verifyTitle: 'Confirmez votre adresse e-mail',
    verifyBody:
      'Pour activer votre offre, nous devons confirmer que cette adresse vous appartient. Saisissez le code reçu par e-mail sur l’écran CYRN Auth.',
    verifyAction: 'Confirmer mon e-mail',
    unavailableTitle: 'Offre indisponible',
    unavailableBody: 'Cette offre n’existe pas ou n’est plus proposée.',
    seePlans: 'Voir les offres',
    alreadyTitle: 'Vous avez déjà une offre pour ce produit',
    alreadyBody: 'Le changement d’offre se fait depuis la page Facturation.',
    goBilling: 'Aller à la facturation',
    doneTitle: 'C’est prêt !',
    doneBody: 'Redirection vers votre espace…',
    open: 'Ouvrir',
    error: 'Une erreur est survenue',
    signedInAs: 'Connecté en tant que',
    billingTitle: 'Facturation',
    individual: 'Particulier',
    business: 'Professionnel',
    siren: 'SIREN',
    sirenHint: 'Les 9 chiffres : nous remplissons le reste.',
    lookup: 'Rechercher',
    sirenUnknown: 'SIREN introuvable : complétez les champs à la main.',
    sirenInvalid: 'Ce SIREN n’est pas valide.',
    lookupDown: 'Annuaire indisponible : complétez les champs à la main.',
    companyClosed: 'Attention : cette entreprise est indiquée comme fermée.',
    legalNameBiz: 'Raison sociale',
    legalNameInd: 'Nom et prénom',
    email: 'E-mail de facturation',
    address: 'Adresse',
    address2: 'Complément (facultatif)',
    postalCode: 'Code postal',
    city: 'Ville',
    country: 'Pays',
    vat: 'N° TVA intracommunautaire (facultatif)',
    edit: 'Modifier',
    save: 'Continuer',
    terms: 'J’accepte les',
    termsLink: 'conditions générales de vente',
    immediate:
      'Je demande l’accès immédiat au service. Je sais que je peux me rétracter dans les 14 jours, et qu’en cas de rétractation je paierai le service fourni jusque-là.',
    renewMonthly: 'Renouvelé chaque mois. Résiliable à tout moment, effet à la fin du mois payé.',
    renewQuarterly:
      'Payé pour 3 mois, renouvelé chaque trimestre. Résiliable à tout moment : l’offre reste active jusqu’à la fin du trimestre payé, sans remboursement, et n’est pas renouvelée.',
    renewYearly:
      'Payé pour 12 mois, renouvelé chaque année. Résiliable à tout moment : l’offre reste active jusqu’à la fin de l’année payée, sans remboursement, et n’est pas renouvelée.',
    payNow: 'Payer',
    secure: 'Paiement sécurisé par Stripe. CYRN ne voit jamais votre numéro de carte.',
    back: 'Retour',
    formFailed: 'Le formulaire de paiement n’a pas pu se charger. Réessayez dans un instant.',
    testMode: 'Mode test : aucun paiement réel.',
    returnWaiting: 'Confirmation du paiement…',
    returnSlow:
      'Paiement reçu, activation en cours. Vous recevrez un e-mail de confirmation ; vous pouvez fermer cette page.',
    returnCancelled: 'Le paiement n’a pas abouti. Vous pouvez réessayer.',
    retry: 'Réessayer',
    required: 'Champ requis',
    registrantTitle: 'Titulaire du nom de domaine',
    registrantHint: 'Le registre exige un contact : il est publié dans l’annuaire Whois, sauf masquage.',
    firstName: 'Prénom',
    lastName: 'Nom',
    phone: 'Téléphone',
    phoneHint: 'Format international, ex. +33612345678',
    phoneInvalid: 'Format attendu : +33612345678',
    renewDomain:
      'Payé pour 1 an, renouvelé chaque année au prix du registre en vigueur, annoncé par e-mail avant le renouvellement. Résiliable à tout moment : le nom reste à vous jusqu’à son expiration.',
    domainTakenTitle: 'Ce nom de domaine n’est plus disponible',
    domainTakenBody: 'Il vient d’être enregistré, ou n’est pas proposé à la vente ici. Essayez un autre nom.',
    priceChanged: 'Le prix du registre pour ce nom vient de changer : vérifiez le nouveau montant avant de payer.',
    domainsClosed: 'L’enregistrement de noms de domaine n’est pas encore ouvert.',
    immediateDomain:
      'Je demande l’enregistrement immédiat de ce nom. Je reconnais qu’une fois le nom enregistré, le service est entièrement exécuté et je perds mon droit de rétractation.',
    prefilled:
      'Nous avons repris vos coordonnées de facturation. Vérifiez-les et corrigez si besoin.',
  },
  en: {
    loading: 'Preparing your order…',
    redirecting: 'Signing in to your CYRN account…',
    summary: 'Your plan',
    free: 'Free',
    perMonth: '/ month',
    perQuarter: '/ quarter',
    perYear: '/ year',
    each: 'each',
    websites: 'websites',
    offer: 'Offer reserved for your organisation',
    monthly: 'Monthly',
    quarterly: 'Quarterly',
    yearly: 'Yearly',
    saveAmount: 'save {amount} ({percent}%)',
    monthsFree: '{n} months free',
    saveSummary: 'You save {amount} ({percent}%)',
    savingVsMonthly: 'compared with paying monthly',
    vatNote: 'VAT not applicable, art. 293 B of the French Tax Code',
    org: 'Organisation',
    newOrg: 'A new organisation',
    start: 'Start for free',
    payUnavailable:
      'Online payment is coming very soon. To subscribe now, write to us at contact@cyrn.fr.',
    verifyTitle: 'Confirm your email address',
    verifyBody:
      'To activate your plan we need to confirm this address is yours. Enter the code sent by email on the CYRN Auth screen.',
    verifyAction: 'Confirm my email',
    unavailableTitle: 'Plan unavailable',
    unavailableBody: 'This plan does not exist or is no longer offered.',
    seePlans: 'See plans',
    alreadyTitle: 'You already have a plan for this product',
    alreadyBody: 'Plan changes are made from the Billing page.',
    goBilling: 'Go to billing',
    doneTitle: 'You’re all set!',
    doneBody: 'Taking you to your workspace…',
    open: 'Open',
    error: 'Something went wrong',
    signedInAs: 'Signed in as',
    billingTitle: 'Billing',
    individual: 'Individual',
    business: 'Business',
    siren: 'SIREN',
    sirenHint: 'The 9 digits: we fill in the rest.',
    lookup: 'Look up',
    sirenUnknown: 'SIREN not found: please fill in the fields.',
    sirenInvalid: 'This SIREN is not valid.',
    lookupDown: 'Company register unavailable: please fill in the fields.',
    companyClosed: 'Note: this company is listed as closed.',
    legalNameBiz: 'Company name',
    legalNameInd: 'Full name',
    email: 'Billing email',
    address: 'Address',
    address2: 'Address line 2 (optional)',
    postalCode: 'Postcode',
    city: 'City',
    country: 'Country',
    vat: 'EU VAT number (optional)',
    edit: 'Edit',
    save: 'Continue',
    terms: 'I accept the',
    termsLink: 'terms of sale',
    immediate:
      'I ask for immediate access to the service. I know I can withdraw within 14 days, and that if I do, I will pay for the service provided until then.',
    renewMonthly: 'Renews every month. Cancel any time; takes effect at the end of the paid month.',
    renewQuarterly:
      'Paid for 3 months, renews every quarter. Cancel any time: the plan stays active until the end of the paid quarter, with no refund, and does not renew.',
    renewYearly:
      'Paid for 12 months, renews every year. Cancel any time: the plan stays active until the end of the paid year, with no refund, and does not renew.',
    payNow: 'Pay',
    secure: 'Secure payment by Stripe. CYRN never sees your card number.',
    back: 'Back',
    formFailed: 'The payment form could not load. Please try again in a moment.',
    testMode: 'Test mode: no real payment.',
    returnWaiting: 'Confirming your payment…',
    returnSlow:
      'Payment received, activating. You will get a confirmation email; you can close this page.',
    returnCancelled: 'The payment did not go through. You can try again.',
    retry: 'Try again',
    required: 'Required',
    registrantTitle: 'Domain holder',
    registrantHint: 'The registry requires a contact: it is published in the Whois directory unless masked.',
    firstName: 'First name',
    lastName: 'Last name',
    phone: 'Phone',
    phoneHint: 'International format, e.g. +33612345678',
    phoneInvalid: 'Expected format: +33612345678',
    renewDomain:
      'Paid for 1 year, renewed every year at the registry’s current price, announced by email before renewal. Cancel any time: the name stays yours until it expires.',
    domainTakenTitle: 'This domain is no longer available',
    domainTakenBody: 'It has just been registered, or is not sold here. Try another name.',
    priceChanged: 'The registry’s price for this name has just changed: check the new amount before paying.',
    domainsClosed: 'Domain registration is not open yet.',
    immediateDomain:
      'I ask for this name to be registered now. I acknowledge that once it is registered the service is fully performed and I lose my right of withdrawal.',
    prefilled: 'We have reused your billing details. Please check them and correct anything that has changed.',
  },
} as const;

function detectLang(param: string | null): Lang {
  const pick = (param || navigator.language || 'fr').slice(0, 2).toLowerCase();
  return pick === 'en' ? 'en' : 'fr';
}

function formatMoney(minor: number, currency: string, lang: Lang): string {
  return new Intl.NumberFormat(lang === 'fr' ? 'fr-FR' : 'en-GB', {
    style: 'currency',
    currency,
    minimumFractionDigits: minor % 100 === 0 ? 0 : 2,
  }).format(minor / 100);
}

function perLabel(interval: PriceInterval, t: Copy): string {
  return interval === 'yearly' ? t.perYear : interval === 'quarterly' ? t.perQuarter : t.perMonth;
}

/** "2 mois offerts" when the saving is whole months, else "économisez 4 € (11 %)". */
function savingLabel(saving: Saving, currency: string, qty: number, t: Copy, lang: Lang): string {
  if (saving.months_free) return t.monthsFree.replace('{n}', String(saving.months_free));
  return t.saveAmount
    .replace('{amount}', formatMoney(saving.amount_minor * qty, currency, lang))
    .replace('{percent}', String(saving.percent));
}

/**
 * One button per period the plan really has, each with its price and what it saves
 * against paying monthly. Nothing to choose, nothing shown.
 */
export function PeriodSwitcher({ prices, current, qty, busy, lang, onPick }: {
  prices: PeriodPrice[];
  current: PriceInterval;
  qty: number;
  busy: boolean;
  lang: Lang;
  onPick: (interval: PriceInterval) => void;
}) {
  const t = T[lang];
  const periods = periodsToOffer(prices);
  if (periods.length < 2) return null;
  return (
    <div role="group" style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
      {periods.map((p) => {
        const saving = savingFor(p, prices);
        return (
          <Button
            key={p.interval}
            size="sm"
            variant={current === p.interval ? 'primary' : 'secondary'}
            aria-pressed={current === p.interval}
            disabled={busy}
            onClick={() => current !== p.interval && onPick(p.interval)}
            style={{ height: 'auto', minHeight: 30, flexWrap: 'wrap' }}
          >
            <span>{t[p.interval]} · {formatMoney(p.amount_minor * qty, p.currency, lang)} {perLabel(p.interval, t)}</span>
            {saving && (
              <span data-saving style={{ fontSize: '0.85em', fontWeight: 'var(--fw-semibold)', opacity: 0.9 }}>
                {savingLabel(saving, p.currency, qty, t, lang)}
              </span>
            )}
          </Button>
        );
      })}
    </div>
  );
}

function errorCode(err: unknown): string | null {
  if (err instanceof ApiError) {
    const detail = (err.data as { detail?: { code?: string } } | null)?.detail;
    return typeof detail === 'object' && detail?.code ? detail.code : null;
  }
  return null;
}

type Copy = (typeof T)[Lang];

/** The name cannot be bought (any more): taken, not for sale, or invalid. */
const DOMAIN_GONE = ['domain_taken', 'domain_unavailable', 'domain_not_registerable', 'domain_invalid', 'quote_invalid'];

function BillingForm({
  initial,
  t,
  busy,
  onSave,
  domain,
}: {
  initial: CheckoutBilling;
  t: Copy;
  busy: boolean;
  onSave: (values: CheckoutBillingInput) => void;
  domain: boolean;
}) {
  const [v, setV] = useState<CheckoutBillingInput>({
    ...initial,
    customer_kind: initial.customer_kind === 'unknown' ? 'individual' : initial.customer_kind,
  });
  const [lookupMsg, setLookupMsg] = useState<string | null>(null);
  const [looking, setLooking] = useState(false);
  const [tried, setTried] = useState(false);
  const biz = v.customer_kind === 'business';
  const set = (k: keyof CheckoutBillingInput, value: string) => setV((p) => ({ ...p, [k]: value }));

  const lookup = async () => {
    const siren = (v.siren || '').replace(/\s/g, '');
    if (!/^\d{9}$/.test(siren)) {
      setLookupMsg(t.sirenInvalid);
      return;
    }
    setLooking(true);
    setLookupMsg(null);
    try {
      const c = await api.checkout.lookupCompany(siren);
      setV((p) => ({
        ...p,
        siren,
        siret: c.siret,
        legal_name: c.legal_name || p.legal_name,
        address_line1: c.address_line1 || p.address_line1,
        address_line2: c.address_line2,
        postal_code: c.postal_code || p.postal_code,
        city: c.city || p.city,
        country: c.country || 'FR',
      }));
      if (!c.active) setLookupMsg(t.companyClosed);
    } catch (err) {
      const code = errorCode(err);
      setLookupMsg(code === 'siren_unknown' ? t.sirenUnknown : code === 'siren_invalid' ? t.sirenInvalid : t.lookupDown);
    } finally {
      setLooking(false);
    }
  };

  const required: (keyof CheckoutBillingInput)[] = [
    'legal_name', 'billing_email', 'address_line1', 'postal_code', 'city', 'country',
    ...(biz && v.country === 'FR' ? (['siren'] as const) : []),
    ...(domain ? (['contact_first_name', 'contact_last_name', 'phone'] as const) : []),
  ];
  const err = (k: keyof CheckoutBillingInput) => (tried && !String(v[k] || '').trim() && required.includes(k) ? t.required : undefined);
  const phone = (v.phone || '').replace(/[\s.()-]/g, '');
  const phoneOk = !domain || /^\+[1-9]\d{6,14}$/.test(phone);

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    setTried(true);
    if (required.some((k) => !String(v[k] || '').trim()) || !phoneOk) return;
    const clean = (x?: string | null) => (x && x.trim() ? x.trim() : null);
    onSave({
      ...v,
      country: (v.country || 'FR').toUpperCase(),
      address_line2: clean(v.address_line2),
      siren: biz ? clean(v.siren)?.replace(/\s/g, '') ?? null : null,
      siret: biz ? clean(v.siret)?.replace(/\s/g, '') ?? null : null,
      vat_number: biz ? clean(v.vat_number) : null,
      ...(domain
        ? { phone, contact_first_name: clean(v.contact_first_name), contact_last_name: clean(v.contact_last_name) }
        : {}),
    });
  };

  return (
    <form onSubmit={submit} style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
      <div style={{ display: 'flex', gap: 8 }}>
        {(['individual', 'business'] as const).map((k) => (
          <Button
            key={k}
            type="button"
            size="sm"
            variant={v.customer_kind === k ? 'primary' : 'secondary'}
            onClick={() => setV((p) => ({ ...p, customer_kind: k }))}
          >
            {k === 'individual' ? t.individual : t.business}
          </Button>
        ))}
      </div>
      {biz && (
        <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end' }}>
          <Input
            label={t.siren}
            value={v.siren || ''}
            mono
            inputMode="numeric"
            maxLength={11}
            hint={lookupMsg || t.sirenHint}
            error={err('siren')}
            onChange={(e) => set('siren', e.target.value)}
            onBlur={() => (v.siren || '').replace(/\s/g, '').length === 9 && !v.legal_name && lookup()}
          />
          <Button type="button" variant="secondary" size="sm" icon={<Search size={14} />} disabled={looking} onClick={lookup}
            style={{ marginBottom: lookupMsg || t.sirenHint ? 22 : 0 }}>
            {t.lookup}
          </Button>
        </div>
      )}
      <Input label={biz ? t.legalNameBiz : t.legalNameInd} value={v.legal_name || ''} error={err('legal_name')}
        autoComplete={biz ? 'organization' : 'name'} onChange={(e) => set('legal_name', e.target.value)} />
      <Input label={t.email} type="email" value={v.billing_email || ''} error={err('billing_email')}
        autoComplete="email" onChange={(e) => set('billing_email', e.target.value)} />
      <Input label={t.address} value={v.address_line1 || ''} error={err('address_line1')}
        autoComplete="address-line1" onChange={(e) => set('address_line1', e.target.value)} />
      <Input label={t.address2} value={v.address_line2 || ''} autoComplete="address-line2"
        onChange={(e) => set('address_line2', e.target.value)} />
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr 70px', gap: 8 }}>
        <Input label={t.postalCode} value={v.postal_code || ''} error={err('postal_code')}
          autoComplete="postal-code" onChange={(e) => set('postal_code', e.target.value)} />
        <Input label={t.city} value={v.city || ''} error={err('city')} autoComplete="address-level2"
          onChange={(e) => set('city', e.target.value)} />
        <Input label={t.country} value={v.country || ''} maxLength={2} error={err('country')}
          autoComplete="country" onChange={(e) => set('country', e.target.value.toUpperCase())} />
      </div>
      {biz && (
        <Input label={t.vat} value={v.vat_number || ''} mono onChange={(e) => set('vat_number', e.target.value)} />
      )}
      {domain && (
        <>
          <div style={{ fontSize: 'var(--text-sm)', fontWeight: 'var(--fw-semibold)', marginTop: 'var(--space-2)' }}>
            {t.registrantTitle}
          </div>
          <p style={{ color: 'var(--text-tertiary)', fontSize: 'var(--text-xs)', marginTop: -6 }}>{t.registrantHint}</p>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
            <Input label={t.firstName} value={v.contact_first_name || ''} error={err('contact_first_name')}
              autoComplete="given-name" onChange={(e) => set('contact_first_name', e.target.value)} />
            <Input label={t.lastName} value={v.contact_last_name || ''} error={err('contact_last_name')}
              autoComplete="family-name" onChange={(e) => set('contact_last_name', e.target.value)} />
          </div>
          <Input label={t.phone} type="tel" value={v.phone || ''} mono autoComplete="tel"
            hint={t.phoneHint}
            error={err('phone') || (tried && v.phone && !phoneOk ? t.phoneInvalid : undefined)}
            onChange={(e) => set('phone', e.target.value)} />
        </>
      )}
      <Button type="submit" size="lg" disabled={busy} icon={<ArrowRight size={18} />} style={{ width: '100%' }}>
        {t.save}
      </Button>
    </form>
  );
}

export function CheckoutPage() {
  const { session } = useAuth();
  const params = useMemo(() => new URLSearchParams(window.location.search), []);
  const lang = detectLang(params.get('lang'));
  const t = T[lang];

  const planCode = params.get('plan') || '';
  const initialInterval = (params.get('interval') as PriceInterval | null) || undefined;
  const orgParam = params.get('org_id') || undefined;
  const ref = params.get('ref') || undefined;
  // Opaque product token (e.g. the site from a cyrn.fr audit); passed through untouched.
  const intent = params.get('intent') || undefined;
  const offer = params.get('offer') || undefined;
  const domainParam = params.get('domain') || undefined;
  const quantityParam = Math.max(1, Math.min(100, parseInt(params.get('quantity') || '1', 10) || 1));

  const [view, setView] = useState<CheckoutView | null>(null);
  const [problem, setProblem] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState<string | null>(null);
  const [editingBilling, setEditingBilling] = useState(false);
  const [acceptTerms, setAcceptTerms] = useState(false);
  const [immediate, setImmediate] = useState(false);
  const [returnState, setReturnState] = useState<'waiting' | 'slow' | 'cancelled' | null>(null);
  const [payment, setPayment] = useState<{ clientSecret: string; publishableKey: string } | null>(null);
  const started = useRef(false);
  const isReturn = window.location.pathname === '/checkout/return';
  const returnId = params.get('checkout_id');

  const returnTo = window.location.pathname + window.location.search;

  const open = useCallback(
    async (opts: { interval?: PriceInterval; org_id?: UUID; new_org?: boolean; quantity?: number }) => {
      setBusy(true);
      setMessage(null);
      try {
        const v = await api.checkout.open({
          plan: planCode,
          interval: opts.interval,
          org_id: opts.org_id,
          new_org: opts.new_org,
          ref,
          intent,
          offer,
          quantity: opts.quantity ?? quantityParam,
          domain: domainParam,
        });
        setView(v);
        setProblem(null);
      } catch (err) {
        const code = errorCode(err);
        if (code) setProblem(code);
        else setMessage(err instanceof Error ? err.message : t.error);
      } finally {
        setBusy(false);
      }
    },
    [planCode, ref, intent, domainParam, t.error]
  );

  useEffect(() => {
    if (started.current) return;
    if (!session) {
      started.current = true;
      // Straight to the Nexus sign-up form. No silent SSO probe first: CYRN Auth
      // answers prompt=none with its login screen when the browser has no
      // session at all, instead of login_required, so a new visitor would land
      // on sign-in. Existing customers use the form's "Sign in" link; with a live
      // SSO session that link signs them in without asking for anything.
      window.location.href = api.auth.signupUrl(returnTo);
      return;
    }
    started.current = true;
    if (!session.email_verified) {
      setProblem('email_unverified');
      return;
    }
    if (isReturn && returnId) {
      pollReturn(returnId);
      return;
    }
    if (!planCode) {
      // /verify-email with a verified address has nothing left to do.
      if (window.location.pathname === '/verify-email') {
        window.location.href = '/';
        return;
      }
      setProblem('plan_unavailable');
      return;
    }
    open({ interval: initialInterval, org_id: orgParam });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session, planCode, initialInterval, orgParam, open, returnTo]);

  // Back from Stripe. The page asks Nexus, which asks Stripe: activation never
  // rests on the redirect alone. The webhook usually wins the race anyway.
  const pollReturn = async (id: string) => {
    setReturnState('waiting');
    for (let i = 0; i < 15; i++) {
      try {
        const v = await api.checkout.refresh(id);
        if (v.launch_url) {
          setDone(v.launch_url);
          window.setTimeout(() => {
            window.location.href = v.launch_url as string;
          }, 1500);
          return;
        }
        if (v.status === 'open') {
          // Stripe page abandoned or expired: back to the order.
          setView(v);
          setReturnState('cancelled');
          return;
        }
      } catch {
        /* keep trying */
      }
      await new Promise((r) => window.setTimeout(r, 2000));
    }
    setReturnState('slow');
  };

  const saveBilling = async (values: CheckoutBillingInput) => {
    if (!view) return;
    setBusy(true);
    setMessage(null);
    try {
      setView(await api.checkout.saveBilling(view.id, values));
      setEditingBilling(false);
    } catch (err) {
      setMessage(err instanceof Error ? err.message : t.error);
    } finally {
      setBusy(false);
    }
  };

  const pay = async () => {
    if (!view) return;
    setBusy(true);
    setMessage(null);
    try {
      const res = await api.checkout.pay(view.id, { accept_terms: acceptTerms, immediate_access: immediate, lang });
      if (res.status === 'active' && res.url) {
        const url = res.url;
        setDone(url);
        window.setTimeout(() => {
          window.location.href = url;
        }, 1200);
      } else if (res.client_secret && res.publishable_key) {
        setPayment({ clientSecret: res.client_secret, publishableKey: res.publishable_key });
      } else {
        setMessage(t.formFailed);
      }
    } catch (err) {
      const code = errorCode(err);
      if (code === 'payment_unavailable') setMessage(t.payUnavailable);
      else if (code === 'already_subscribed') setProblem(code);
      else if (code === 'billing_incomplete') setEditingBilling(true);
      else if (code === 'price_changed') {
        // The checkout now carries the new price: show it before the customer pays.
        setView(await api.checkout.get(view.id));
        setMessage(t.priceChanged);
      } else if (code && DOMAIN_GONE.includes(code)) setProblem('domain_unavailable');
      else setMessage(err instanceof Error ? err.message : t.error);
    }
    setBusy(false);
  };

  const confirm = async () => {
    if (!view) return;
    setBusy(true);
    setMessage(null);
    try {
      const res = await api.checkout.confirm(view.id);
      setDone(res.launch_url);
      window.setTimeout(() => {
        window.location.href = res.launch_url;
      }, 1200);
    } catch (err) {
      const code = errorCode(err);
      if (code === 'payment_unavailable') setMessage(t.payUnavailable);
      else if (code && code !== 'payment_unavailable') setProblem(code);
      else setMessage(err instanceof Error ? err.message : t.error);
    } finally {
      setBusy(false);
    }
  };

  const shell = (children: React.ReactNode) => (
    <div
      style={{
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 'var(--space-6)',
        background: 'radial-gradient(ellipse at top, #111927 0%, #070b11 100%)',
      }}
    >
      <div style={{ width: '100%', maxWidth: 480, display: 'flex', flexDirection: 'column', gap: 'var(--space-5)' }}>
        <div style={{ display: 'flex', justifyContent: 'center' }}>
          <Logo variant="stacked" size={44} light="beams" />
        </div>
        {children}
      </div>
    </div>
  );

  if (!session) return shell(<Loader size={28} label={t.redirecting} />);

  if (problem === 'email_unverified') {
    return shell(
      <Card padding="var(--space-6)" glow style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}>
        <MailCheck size={28} color="var(--accent)" />
        <h2 style={{ fontSize: 'var(--text-lg)', fontWeight: 'var(--fw-semibold)' }}>{t.verifyTitle}</h2>
        <p style={{ color: 'var(--text-secondary)', fontSize: 'var(--text-sm)' }}>{t.verifyBody}</p>
        {session.principal.email && (
          <p style={{ color: 'var(--text-tertiary)', fontSize: 'var(--text-xs)' }}>
            {t.signedInAs} <strong>{session.principal.email}</strong>
          </p>
        )}
        <Button
          size="lg"
          icon={<ArrowRight size={18} />}
          onClick={() => {
            window.location.href = api.auth.loginReturnUrl(returnTo, 'login');
          }}
        >
          {t.verifyAction}
        </Button>
      </Card>
    );
  }

  if (problem === 'plan_unavailable') {
    return shell(
      <Card padding="var(--space-6)" style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}>
        <h2 style={{ fontSize: 'var(--text-lg)', fontWeight: 'var(--fw-semibold)' }}>{t.unavailableTitle}</h2>
        <p style={{ color: 'var(--text-secondary)', fontSize: 'var(--text-sm)' }}>{t.unavailableBody}</p>
        <Button variant="secondary" onClick={() => { window.location.href = 'https://cyrn.fr'; }}>
          {t.seePlans}
        </Button>
      </Card>
    );
  }

  if (problem && DOMAIN_GONE.includes(problem)) {
    return shell(
      <Card padding="var(--space-6)" style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}>
        <h2 style={{ fontSize: 'var(--text-lg)', fontWeight: 'var(--fw-semibold)' }}>{t.domainTakenTitle}</h2>
        <p style={{ color: 'var(--text-secondary)', fontSize: 'var(--text-sm)' }}>
          {domainParam && <strong style={{ fontFamily: 'var(--font-mono)' }}>{domainParam}</strong>}{' '}
          {t.domainTakenBody}
        </p>
      </Card>
    );
  }

  if (problem === 'domains_unavailable' || problem === 'registrar_unavailable') {
    return shell(<Alert tone="info" icon={<AlertCircle size={18} />}>{t.domainsClosed}</Alert>);
  }

  if (problem === 'already_subscribed') {
    return shell(
      <Card padding="var(--space-6)" style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}>
        <h2 style={{ fontSize: 'var(--text-lg)', fontWeight: 'var(--fw-semibold)' }}>{t.alreadyTitle}</h2>
        <p style={{ color: 'var(--text-secondary)', fontSize: 'var(--text-sm)' }}>{t.alreadyBody}</p>
        <Button variant="secondary" onClick={() => { window.location.href = '/billing'; }}>
          {t.goBilling}
        </Button>
      </Card>
    );
  }

  if (done) {
    return shell(
      <Card padding="var(--space-6)" glow style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)', alignItems: 'center', textAlign: 'center' }}>
        <Check size={32} color="var(--success)" />
        <h2 style={{ fontSize: 'var(--text-lg)', fontWeight: 'var(--fw-semibold)' }}>{t.doneTitle}</h2>
        <p style={{ color: 'var(--text-secondary)', fontSize: 'var(--text-sm)' }}>{t.doneBody}</p>
        <a href={done} style={{ color: 'var(--accent)', fontSize: 'var(--text-sm)' }}>{t.open}</a>
      </Card>
    );
  }

  if ((isReturn && returnState !== 'cancelled') || returnState === 'waiting' || returnState === 'slow') {
    return shell(
      <Card padding="var(--space-6)" style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)', alignItems: 'center', textAlign: 'center' }}>
        {returnState === 'slow' ? (
          <>
            <Check size={28} color="var(--success)" />
            <p style={{ color: 'var(--text-secondary)', fontSize: 'var(--text-sm)' }}>{t.returnSlow}</p>
          </>
        ) : (
          <Loader size={28} label={t.returnWaiting} />
        )}
      </Card>
    );
  }

  if (!view) {
    return shell(
      <>
        {message && <Alert tone="danger" icon={<AlertCircle size={18} />}>{message}</Alert>}
        {!message && <Loader size={28} label={t.loading} />}
      </>
    );
  }

  const copy = view.translations[lang] || view.translations.fr || {};
  const productCopy = view.product_translations[lang] || view.product_translations.fr || {};
  const per = perLabel(view.interval, t);
  const qty = view.quantity || 1;
  const prices = view.prices || [];
  const currentPrice = prices.find((p) => p.interval === view.interval);
  const saving = currentPrice ? savingFor(currentPrice, prices) : null;

  return shell(
    <Card padding="var(--space-6)" glow style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-5)' }}>
      <div>
        <div style={{ fontSize: 'var(--text-xs)', color: 'var(--text-tertiary)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
          {t.summary}
        </div>
        <h1 style={{ fontSize: 'var(--text-xl)', fontWeight: 'var(--fw-semibold)', marginTop: 4 }}>
          {view.domain ? (
            <span style={{ fontFamily: 'var(--font-mono)' }}>{view.domain}</span>
          ) : (
            <>{productCopy.name} {copy.name}</>
          )}
        </h1>
        {copy.tagline && (
          <p style={{ color: 'var(--text-secondary)', fontSize: 'var(--text-sm)', marginTop: 4 }}>{copy.tagline}</p>
        )}
      </div>

      <div style={{ display: 'flex', alignItems: 'baseline', gap: 6 }}>
        <span style={{ fontSize: '2rem', fontWeight: 'var(--fw-semibold)' }}>
          {view.is_free ? t.free : formatMoney(view.amount_minor * qty, view.currency, lang)}
        </span>
        {!view.is_free && <span style={{ color: 'var(--text-tertiary)' }}>{per}</span>}
      </div>
      {qty > 1 && (
        <div style={{ color: 'var(--text-secondary)', fontSize: 'var(--text-sm)', marginTop: -12 }}>
          {qty} {view.product_code === 'hosting' ? t.websites : '×'} · {formatMoney(view.amount_minor, view.currency, lang)}{' '}
          {t.each}
        </div>
      )}
      {!view.is_free && !view.domain && saving && (
        <div data-saving-summary style={{ color: 'var(--accent)', fontSize: 'var(--text-sm)', marginTop: -12 }}>
          {t.saveSummary
            .replace('{amount}', formatMoney(saving.amount_minor * qty, view.currency, lang))
            .replace('{percent}', String(saving.percent))}
          {saving.months_free ? <> · {t.monthsFree.replace('{n}', String(saving.months_free))}</> : null}{' '}
          <span style={{ color: 'var(--text-tertiary)' }}>{t.savingVsMonthly}</span>
        </div>
      )}
      {view.offer_id && (
        <div style={{ color: 'var(--accent)', fontSize: 'var(--text-xs)', marginTop: -12 }}>{t.offer}</div>
      )}

      {!view.is_free && !view.domain && (
        <PeriodSwitcher
          prices={prices}
          current={view.interval}
          qty={qty}
          busy={busy}
          lang={lang}
          onPick={(iv) => open({ interval: iv, org_id: view.org_id || undefined, quantity: qty })}
        />
      )}

      {copy.features && copy.features.length > 0 && (
        <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: 8 }}>
          {copy.features.map((f) => (
            <li key={f} style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 'var(--text-sm)' }}>
              <Check size={16} color="var(--accent)" /> {f}
            </li>
          ))}
        </ul>
      )}

      {view.org_options.length > 1 && (
        <label style={{ display: 'flex', flexDirection: 'column', gap: 6, fontSize: 'var(--text-xs)', color: 'var(--text-secondary)' }}>
          <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <Building2 size={14} /> {t.org}
          </span>
          <select
            value={view.org_id || ''}
            disabled={busy}
            onChange={(e) =>
              e.target.value === '__new__'
                ? open({ interval: view.interval, new_org: true, quantity: qty })
                : open({ interval: view.interval, org_id: e.target.value, quantity: qty })
            }
            style={{
              padding: '9px 12px',
              background: 'var(--bg-inset)',
              color: 'var(--text-primary)',
              border: '1px solid var(--border-default)',
              borderRadius: 'var(--radius-md)',
              fontSize: 'var(--text-sm)',
            }}
          >
            {view.org_options.map((o) => (
              <option key={o.id} value={o.id}>{o.display_name}</option>
            ))}
            <option value="__new__">{t.newOrg}</option>
          </select>
        </label>
      )}

      {returnState === 'cancelled' && <Alert tone="warning" icon={<AlertCircle size={18} />}>{t.returnCancelled}</Alert>}
      {message && <Alert tone="warning" icon={<AlertCircle size={18} />}>{message}</Alert>}

      {view.is_free ? (
        <Button size="lg" icon={<ArrowRight size={18} />} disabled={busy} onClick={confirm} style={{ width: '100%' }}>
          {t.start}
        </Button>
      ) : !view.payments_enabled ? (
        <Alert tone="info" icon={<AlertCircle size={18} />}>{t.payUnavailable}</Alert>
      ) : payment ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
          {view.test_mode && (
            <Alert tone="info" icon={<AlertCircle size={18} />}>{t.testMode}</Alert>
          )}
          <EmbeddedPayment
            publishableKey={payment.publishableKey}
            clientSecret={payment.clientSecret}
            onComplete={() => {
              setPayment(null);
              pollReturn(view.id);
            }}
            onError={() => {
              setPayment(null);
              setMessage(t.formFailed);
            }}
          />
          <Button variant="secondary" size="sm" onClick={() => setPayment(null)}>
            {t.back}
          </Button>
        </div>
      ) : view.billing && (editingBilling || view.billing.missing.length > 0) ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
          <div style={{ fontSize: 'var(--text-sm)', fontWeight: 'var(--fw-semibold)' }}>{t.billingTitle}</div>
          {/* Say where the values came from. Presenting another organisation's
              address as this one's own, unmarked, would be putting words in the
              customer's mouth on a document they are about to be invoiced on. */}
          {view.billing.suggested && (
            <Alert tone="info" icon={<AlertCircle size={18} />}>{t.prefilled}</Alert>
          )}
          <BillingForm initial={view.billing} t={t} busy={busy} onSave={saveBilling} domain={!!view.domain} />
        </div>
      ) : (
        view.billing && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12,
              padding: '10px 12px', background: 'var(--bg-inset)', borderRadius: 'var(--radius-md)', fontSize: 'var(--text-xs)' }}>
              <div style={{ color: 'var(--text-secondary)', lineHeight: 1.5 }}>
                <strong style={{ color: 'var(--text-primary)' }}>{view.billing.legal_name}</strong>
                {view.billing.siren && <> · SIREN {view.billing.siren}</>}
                <br />
                {view.billing.address_line1}, {view.billing.postal_code} {view.billing.city}
                <br />
                {view.billing.billing_email}
              </div>
              <button type="button" onClick={() => setEditingBilling(true)}
                style={{ background: 'none', border: 'none', color: 'var(--accent)', cursor: 'pointer', fontSize: 'var(--text-xs)' }}>
                {t.edit}
              </button>
            </div>

            <p style={{ color: 'var(--text-tertiary)', fontSize: 'var(--text-xs)', lineHeight: 1.5 }}>
              {view.domain
                ? t.renewDomain
                : view.interval === 'yearly'
                  ? t.renewYearly
                  : view.interval === 'quarterly'
                    ? t.renewQuarterly
                    : t.renewMonthly}
            </p>

            <label style={{ display: 'flex', gap: 8, fontSize: 'var(--text-xs)', color: 'var(--text-secondary)', alignItems: 'flex-start' }}>
              <input type="checkbox" checked={acceptTerms} onChange={(e) => setAcceptTerms(e.target.checked)} />
              <span>
                {t.terms}{' '}
                <a href={TERMS_URL} target="_blank" rel="noopener noreferrer" style={{ color: 'var(--accent)' }}>{t.termsLink}</a>
              </span>
            </label>
            {view.billing.customer_kind === 'individual' && (
              <label style={{ display: 'flex', gap: 8, fontSize: 'var(--text-xs)', color: 'var(--text-secondary)', alignItems: 'flex-start', lineHeight: 1.5 }}>
                <input type="checkbox" checked={immediate} onChange={(e) => setImmediate(e.target.checked)} />
                <span>{view.domain ? t.immediateDomain : t.immediate}</span>
              </label>
            )}

            <Button
              size="lg"
              icon={<Lock size={16} />}
              disabled={busy || !acceptTerms || (view.billing.customer_kind === 'individual' && !immediate)}
              onClick={pay}
              style={{ width: '100%' }}
            >
              {t.payNow} {formatMoney(view.total_minor, view.currency, lang)}
            </Button>
            <p style={{ color: 'var(--text-tertiary)', fontSize: '11px', textAlign: 'center' }}>
              {t.secure}
              {view.test_mode && <> · <strong>{t.testMode}</strong></>}
            </p>
          </div>
        )
      )}

      {!view.is_free && (
        <p style={{ color: 'var(--text-tertiary)', fontSize: '11px', textAlign: 'center' }}>{t.vatNote}</p>
      )}
    </Card>
  );
}
